import type { TrackerImportRow } from '../services/tracker-api-client';

/**
 * Client-side parser for a Partiful guest-list export.
 *
 * Columns: `Name,Status,RSVP date,Invited By,Is Plus One Of`. There is no email
 * column — hosts add addresses by hand in the record editor afterwards.
 *
 * Two things real exports contain that a naive split misses: a NON-BREAKING
 * SPACE inside status values (Can't, then U+00A0, then Go), and curly quotes.
 * Both are normalized here and again server-side, because a status that fails
 * to match silently downgrades a guest to "invited".
 *
 * Names are quoted when they contain commas (`"Carly, Ryan, Meg"`), so the row
 * splitter has to honour RFC 4180 quoting rather than splitting on commas.
 */

export interface PartifulParseResult {
  rows: TrackerImportRow[];
  /** Rows the file contained but this parser could not use, with the reason. */
  skipped: { line: number; reason: string }[];
  headers: string[];
}

/**
 * NBSP → space, curly quotes → straight, collapse runs, trim.
 *
 * The escapes are deliberate: a real Partiful export writes "Can\u2019t\u00a0Go"
 * with a non-breaking space and a curly apostrophe, and both are invisible in a
 * diff. Written as literal characters they get "helpfully" normalized by an
 * editor or a copy-paste and the status silently stops matching, which
 * downgrades a guest to "invited".
 */
export function normalizeCsvText(value: string): string {
  return value
    .replace(/[\u00a0\u2007\u202f]/g, ' ')
    .replace(/[\u2018\u2019\u201b]/g, "'")
    .replace(/[\u201c\u201d]/g, '"')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Comma or tab, decided by the header line.
 *
 * "Download as TSV" and a copy out of a spreadsheet are both tab-separated, and
 * parsing one with a comma splitter yields a single column per row — which then
 * fails as "no name column", sending the host to look for a problem in a file
 * that was fine. Sniffing the first line costs nothing and makes that
 * unreachable. Quoted fields can contain either character, so only the header is
 * consulted, and a tie goes to comma.
 */
function sniffDelimiter(source: string): ',' | '\t' {
  const firstLine = source.split(/\r?\n/, 1)[0] ?? '';
  const tabs = (firstLine.match(/\t/g) ?? []).length;
  const commas = (firstLine.match(/,/g) ?? []).length;
  return tabs > commas ? '\t' : ',';
}

/** Splits a whole document into rows of fields, honouring quotes and CRLF. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  // A UTF-8 BOM would otherwise become part of the first header name.
  const source = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const delimiter = sniffDelimiter(source);

  for (let index = 0; index < source.length; index += 1) {
    const char = source[index];

    if (quoted) {
      if (char === '"') {
        if (source[index + 1] === '"') {
          field += '"';
          index += 1;
        } else {
          quoted = false;
        }
      } else {
        field += char;
      }
      continue;
    }

    if (char === '"') {
      quoted = true;
    } else if (char === delimiter) {
      row.push(field);
      field = '';
    } else if (char === '\n' || char === '\r') {
      // Swallow the LF of a CRLF pair so it does not open an empty row.
      if (char === '\r' && source[index + 1] === '\n') index += 1;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else {
      field += char;
    }
  }

  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((entry) => entry.some((value) => value.trim().length > 0));
}

function headerIndex(headers: string[], ...candidates: string[]): number {
  const normalized = headers.map((header) => normalizeCsvText(header).toLowerCase());
  for (const candidate of candidates) {
    const index = normalized.indexOf(candidate.toLowerCase());
    if (index >= 0) return index;
  }
  return -1;
}

/**
 * Partiful writes "8/12/2026" or an ISO instant depending on the export. Only
 * values that parse to a real date are forwarded; anything else is dropped
 * rather than guessed, because a wrong RSVP timestamp is worse than none.
 */
function toIsoInstant(value: string): string | null {
  const trimmed = normalizeCsvText(value);
  if (!trimmed) return null;
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString();
}

export function parsePartifulCsv(text: string): PartifulParseResult {
  const table = parseCsv(text);
  if (table.length === 0) return { rows: [], skipped: [], headers: [] };

  const headers = table[0].map((header) => normalizeCsvText(header));
  const nameAt = headerIndex(headers, 'name', 'guest', 'full name');
  const statusAt = headerIndex(headers, 'status', 'rsvp', 'rsvp status');
  const rsvpAtAt = headerIndex(headers, 'rsvp date', 'rsvp_at', 'responded at');
  const invitedByAt = headerIndex(headers, 'invited by', 'invited_by', 'host');
  const plusOneAt = headerIndex(headers, 'is plus one of', 'plus one of', 'plus_one_of');
  const emailAt = headerIndex(headers, 'email', 'email address');
  // Our own export coming back. Its presence changes what the file means: the
  // rows are this trip's guests rather than a foreign roster, so they are matched
  // by id and the RSVP rules below relax.
  const tripsbyIdAt = headerIndex(headers, 'tripsby id', 'tripsby_id');
  const isRoundTrip = tripsbyIdAt >= 0;

  const rows: TrackerImportRow[] = [];
  const skipped: { line: number; reason: string }[] = [];

  if (nameAt < 0) {
    return { rows, skipped: [{ line: 1, reason: 'No Name column in this file.' }], headers };
  }

  // A file with no status column would send `status: null` for every row, and
  // the server reads that as "no answer" and resets the whole roster to
  // invited. This is not hypothetical: our own export writes a "Going" column,
  // so a host who exports and re-imports would wipe the RSVPs they just took.
  // "Going" is deliberately NOT accepted as a status column — it renders
  // `going_override ?? rsvp_status`, so importing it would push a host's
  // override down into the guest's own answer.
  // A Tripsby export is exempt: it carries no Status column by design, and its
  // rows send no status at all, which the server reads as "leave the stored RSVP
  // alone". That is the whole point of the round trip — a host exports the
  // roster, fills in the blank emails in a spreadsheet and sends it back, and
  // the answers people already gave are none of that file's business.
  if (statusAt < 0 && !isRoundTrip) {
    return {
      rows,
      skipped: [
        {
          line: 1,
          reason:
            'No RSVP column in this file. It has to be named Status, RSVP or RSVP status — ' +
            'importing without one would clear every RSVP on the list.',
        },
      ],
      headers,
    };
  }

  const cell = (record: string[], index: number): string =>
    index >= 0 && index < record.length ? normalizeCsvText(record[index]) : '';

  table.slice(1).forEach((record, offset) => {
    const line = offset + 2; // 1-based, and the header occupies line 1.
    const name = cell(record, nameAt);
    if (!name) {
      skipped.push({ line, reason: 'Row has no name.' });
      return;
    }

    const email = cell(record, emailAt);
    const status = cell(record, statusAt);
    const rsvpAt = rsvpAtAt >= 0 ? toIsoInstant(cell(record, rsvpAtAt)) : null;
    const invitedBy = cell(record, invitedByAt);
    const plusOneOf = cell(record, plusOneAt);

    rows.push({
      // Passed through as written. A cell someone cleared or garbled in a
      // spreadsheet is not a match the server should honour, and it refuses an
      // id that is not on this trip — so the row falls back to email or name
      // rather than landing on a stranger.
      inviteeId: isRoundTrip ? cell(record, tripsbyIdAt) || null : null,
      name,
      email: email || null,
      status: status || null,
      rsvpAt,
      invitedBy: invitedBy || null,
      plusOneOf: plusOneOf || null,
    });
  });

  return { rows, skipped, headers };
}

/**
 * Local preview of how the server will read each status, so the host sees the
 * split before importing. Mirrors `GroupTrackerRsvp.FromImportStatus` — the
 * server remains authoritative.
 */
export function previewImportStatus(status?: string | null): string {
  // A blank cell in a file that HAS a status column posts null, and the server
  // treats an absent status as "leave the stored RSVP alone" (COALESCE). Counting
  // those as Invited would show the host a number the import will not produce, so
  // they get their own bucket.
  if (status === null || status === undefined || normalizeCsvText(status) === '') {
    return 'unchanged';
  }
  const value = normalizeCsvText(status).toLowerCase();
  if (['going', 'yes', 'attending', 'confirmed'].includes(value)) return 'going';
  if (value === 'maybe') return 'maybe';
  if (["can't go", 'cant go', 'cant_go', 'no', 'not_going', 'declined'].includes(value)) {
    return 'cant_go';
  }
  return 'invited';
}

export function summarizeImportRows(rows: readonly TrackerImportRow[]) {
  const counts = { going: 0, maybe: 0, cant_go: 0, invited: 0, unchanged: 0 };
  let plusOnes = 0;
  for (const row of rows) {
    counts[previewImportStatus(row.status) as keyof typeof counts] += 1;
    if (row.plusOneOf) plusOnes += 1;
  }
  return { total: rows.length, counts, plusOnes };
}
